// Editor.tsx
import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react"
import { useBlocker } from "react-router-dom"
import { useEditor, EditorContent, Editor as TiptapEditor } from "@tiptap/react"
import { DOMParser as ProseMirrorDOMParser } from "@tiptap/pm/model"
import { Tour } from "antd"

import QuestionnaireCheckDisplay from "./QuestionnaireCheckDisplay.component"
import QuestionnaireMenuBar from "features/QuestionnaireEditor/components/Editor/MenuBar/QuestionnaireMenuBar.component"
import EditorFooter from "./EditorFooter.component"
import EditorStartScreen from "./StartScreen/EditorStartScreen.component"
import { useStartScreen } from "./StartScreen/useStartScreen"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import StructurePanel from "features/QuestionnaireEditor/components/Structure/StructurePanel.component"
import ElementHighlight from "./ElementHighlight.component"
import EditorContextMenu from "./ContextMenu/EditorContextMenu.component"
import ListPickerModal from "./ContextMenu/ListPickerModal.component"
import TimingModal from "./ContextMenu/TimingModal.component"
import { LogicWizard } from "./LogicWizard"
import { RecodeWizard } from "./RecodeWizard"
import { UsagesPanel } from "./ContextMenu/UsagesPanel.component"
import { ExceptionsUsagesPanel } from "./ContextMenu/ExceptionsUsagesPanel.component"
import EditorCommentsUI from "./EditorCommentsUI.component"
import actions from "./MenuBar/MenuActions"

import { escapeHTML } from "utils/escapeHtml"
import { getCollaborationWsUrl } from "utils/server"
import { useQuestionnaire } from "features/QuestionnaireEditor/api/useQuestionnaire"
import { useEditorContextMenu } from "features/QuestionnaireEditor/hooks/useEditorContextMenu"
import { useTour } from "features/QuestionnaireEditor/hooks/useTour"
import { useLineNumbers } from "features/QuestionnaireEditor/hooks/useLineNumbers"
import { useEditorExtensions } from "features/QuestionnaireEditor/hooks/useEditorExtensions"
import { useCollaborativeSession } from "features/QuestionnaireEditor/hooks/useCollaborativeSession"
import { useYjsContentSync } from "features/QuestionnaireEditor/hooks/useYjsContentSync"
import { useQuestionnaireSave } from "features/QuestionnaireEditor/hooks/useQuestionnaireSave"
import { useEditorWizards } from "features/QuestionnaireEditor/hooks/useEditorWizards"
import { useEditorUIState } from "features/QuestionnaireEditor/hooks/useEditorUIState"
import { usePendingScroll } from "features/QuestionnaireEditor/hooks/usePendingScroll"
import { useDragResize } from "features/QuestionnaireEditor/hooks/useDragResize"
import { useComments } from "features/QuestionnaireEditor/hooks/useComments"
import { VersionHistoryPanel } from "features/Provenance"
import { useCommentsStore } from "stores/commentsStore"
import { useVersionHistory } from "features/QuestionnaireEditor/hooks/useVersionHistory"
import {
  Variant,
  EditorRef,
  LoadedQuestionnaire,
  NotificationError,
  NotificationWarning,
  EditorSaveOptions,
} from "types/questionnaire.types"
import { StagedBlock } from "types/template.types"

// Define the props interface for the Editor component
interface EditorProps {
  /** The initial content of the questionnaire */
  questionnaire: string
  /** Function to save the questionnaire content; `options.checkpoint` for a save that cuts a version */
  saveQuestionnaire: (
    updatedVariantsParam?: Variant[],
    collaborativeContent?: string,
    options?: EditorSaveOptions,
  ) => Promise<void>
  /** The variants data that will be passed to the export form */
  variants: Variant[]
  /** The questionnaire's own languages (its #LANGUAGE line above any variant), for the test survey on Master */
  masterLanguages: string[]
  /** Indicates if the template should be used in the master configuration */
  inMaster: boolean
  /** Function to retrieve the notes for the questionnaire */
  getQuestionnaireNotes: () => string[]
  /** Optional callback to open labels modal with specific label */
  onOpenLabelsModal?: (labelName?: string) => void
  /** Ref to Labels button for tour */
  labelsButtonRef?: React.RefObject<HTMLElement | null>
  /** Callback to manually start the tour */
  onStartTour?: () => void
  /** Project ID for collaboration */
  projectId?: string
  /** User ID for collaboration */
  userId?: string
  /** Project owner (Created_by) for API calls */
  owner?: string
  /** Current selected variant name */
  currentVariant?: string
  /** Full questionnaire text content */
  fullQuestionnaire?: string
  /** Loads a full questionnaire text (variants included) the way the editor opens one; returns each part as editor HTML */
  loadQuestionnaireText: (text: string) => LoadedQuestionnaire
  /** WebSocket URL for collaboration (optional) */
  websocketUrl?: string
  /** Function to fetch the latest questionnaire from backend */
  fetchLatestQuestionnaire?: () => Promise<string>
  /** Function to switch to a specific variant */
  onSwitchVariant?: (variant: Variant | { name: string }) => void
  /** Pending scroll line number from parent */
  pendingScrollLine?: number | null
  /** Function to clear pending scroll */
  onClearPendingScroll?: () => void
  /** Function to set pending scroll in parent */
  onSetPendingScroll?: (lineNumber: number) => void
  /** Whether the check display panel is enabled */
  checkingQuestionnaireEnabled?: boolean
  /** Function to set check display panel enabled state */
  setCheckingQuestionnaireEnabled?: (enabled: boolean) => void
  /** Check questionnaire errors */
  checkQuestionnaireErrors?: NotificationError[]
  /** Function to set check questionnaire errors */
  setCheckQuestionnaireErrors?: (errors: NotificationError[]) => void
  /** Check questionnaire warnings */
  checkQuestionnaireWarnings?: NotificationWarning[]
  /** Function to set check questionnaire warnings */
  setCheckQuestionnaireWarnings?: (warnings: NotificationWarning[]) => void
  /** Callback when connection state changes (for disabling parent UI during connection) */
  onConnectionStateChange?: (isConnecting: boolean) => void
  /** The questionnaire-wide controls (VariantsMenuBar) for the toolbar's top row */
  toolbarExtras?: React.ReactNode
  /** Filled by the menu bar: opens the Templates wizard filled in with a block (assistant proposals) */
  templateProposalRef?: React.MutableRefObject<
    ((block: StagedBlock, onStaged?: () => void) => void) | null
  >
}

/**
 * Editor component provides a rich text editor for questionnaires.
 * It integrates with various extensions and allows for content manipulation, saving, and validation.
 *
 * @component
 * @param props - The component props
 * @param ref - The ref object for the component to expose methods
 * @returns The rendered Editor component
 */
const Editor = forwardRef<EditorRef, EditorProps>((props, ref) => {
  const {
    questionnaire,
    saveQuestionnaire,
    variants,
    masterLanguages,
    inMaster,
    getQuestionnaireNotes,
    onOpenLabelsModal,
    labelsButtonRef,
    onStartTour,
    projectId,
    userId,
    owner = "",
    currentVariant = "",
    fullQuestionnaire = "",
    loadQuestionnaireText,
    websocketUrl,
    fetchLatestQuestionnaire,
    onSwitchVariant,
    pendingScrollLine,
    onClearPendingScroll,
    onSetPendingScroll,
    checkingQuestionnaireEnabled = false,
    setCheckingQuestionnaireEnabled,
    checkQuestionnaireErrors = [],
    setCheckQuestionnaireErrors,
    checkQuestionnaireWarnings = [],
    setCheckQuestionnaireWarnings,
    onConnectionStateChange,
    toolbarExtras,
    templateProposalRef,
  } = props

  const { isCheckingQuestionnaire, checkQuestionnaireByID } = useQuestionnaire()

  // Typed refs
  const editorContainerRef = useRef<HTMLDivElement>(null)

  // Check display resize hook
  const { height: checkDisplayHeight, handleResize: handleCheckDisplayResize } =
    useDragResize({
      initialHeight: 30,
      minHeight: 10,
      maxHeight: 70,
      containerRef: editorContainerRef,
    })
  const lineNumbersRef = useRef<HTMLDivElement>(null)
  const structurePanelButtonRef = useRef<HTMLButtonElement>(null)
  const addElementButtonRef = useRef<HTMLButtonElement>(null)
  const elementHighlightButtonRef = useRef<HTMLButtonElement>(null)

  // Create internal ref for structure panel
  const internalEditorRef = useRef<EditorRef>({
    insertContentAtEnd: () => {},
    getHTML: () => "",
    setHTML: () => {},
    scrollToLine: () => {},
    getEditor: () => null,
  })

  // Check if collaboration is enabled
  const collaborationEnabled = Boolean(projectId && userId)

  // Collaboration session lifecycle (connection state, variant Yjs docs)
  const {
    isConnected,
    isConnecting,
    isCollaborationReady,
    currentDoc,
    setCurrentDoc,
    currentDocRef,
    pendingVariantSwitchRef,
    populatedVariantsRef,
    switchToVariant,
    convertToTextFormat,
    getProvider,
    collaborationService,
  } = useCollaborativeSession({
    collaborationEnabled,
    projectId,
    userId,
    websocketUrl,
    variants,
    currentVariant,
    onConnectionStateChange,
  })

  // Typed state variables
  const [editorContent, setEditorContent] = useState<string>(questionnaire)
  const [contentChanged, setContentChanged] = useState<boolean>(false)

  // Track if editor has been fully initialized to ignore initial content load updates
  const isEditorReadyRef = useRef<boolean>(false)

  const qnrEmpty: boolean = questionnaire === "<p></p>"

  // Initialize tour
  const {
    isTourActive,
    tourSteps,
    startTour,
    handleTourClose,
    handleTourFinish,
  } = useTour({
    labelsButtonRef,
    structurePanelButtonRef,
    addElementButtonRef,
    elementHighlightButtonRef,
    editorContainerRef,
    // Tour disabled for now — steps need revising for the new features.
    autoStart: false,
  })

  // The start screen's close, for the tour (set once the editor exists, below)
  const startScreenDismissRef = useRef<(() => void) | null>(null)

  // Expose startTour to parent if provided
  useEffect(() => {
    if (onStartTour) {
      // Store reference to startTour in parent
      // Starting the tour closes the start screen: its steps point at the editor.
      window.dataqStartTour = () => {
        startScreenDismissRef.current?.()
        startTour()
      }
    }
  }, [onStartTour, startTour])

  // Get comment store actions for the extension callback
  const {
    setActiveComment: storeSetActiveComment,
    openPanel: storeOpenPanel,
    hasNewComments,
    newCommentIds,
    exportAuditLog,
  } = useCommentsStore()

  // Callback for when a comment is clicked in the editor
  const onCommentActivatedCallback = useCallback(
    (commentId: string | null) => {
      storeSetActiveComment(commentId)
      if (commentId) {
        storeOpenPanel()
      }
    },
    [storeSetActiveComment, storeOpenPanel],
  )

  // Live editor instance for the UI-state hook. A ref because that hook must
  // run before useEditor (its highlightingEnabled feeds the extensions);
  // assigned during render right after useEditor below.
  const editorInstanceRef = useRef<TiptapEditor | null>(null)

  // Editor UI state (view toggles, structure panel, add-element modal)
  const {
    highlightingEnabled,
    lineNumbersEnabled,
    elementHighlightingEnabled,
    structurePanelVisible,
    addElementModalVisible,
    structureJumpToLine,
    toggleHighlighting,
    toggleLineNumbers,
    toggleElementHighlighting,
    toggleStructurePanel,
    toggleAddElementModal,
    setAddElementModalVisible,
    setStructureJumpToLine,
    jumpToStructure,
  } = useEditorUIState({
    editorRef: editorInstanceRef,
    setEditorContent,
  })

  // Configure editor extensions using extracted hook
  const extensions = useEditorExtensions({
    highlightingEnabled,
    isCollaborationReady,
    collaborationEnabled,
    currentDoc,
    currentVariant,
    userId,
    getProvider,
    onCommentActivated: onCommentActivatedCallback,
    commentsEnabled: true,
  })

  const editor: TiptapEditor | null = useEditor(
    {
      extensions,
      editorProps: {
        attributes: {
          // Disable the browser's native spell-check (red squiggles). The
          // questionnaire is structured DATA(Q) syntax and labels, not prose,
          // so spell-check only produces noisy false positives.
          spellcheck: "false",
        },
      },
      // In collaborative mode, don't pass content - Yjs manages it
      // Content will be populated immediately after editor creation
      content:
        collaborationEnabled && isCollaborationReady
          ? undefined
          : editorContent,
      onUpdate: ({ transaction }) => {
        // Skip updates during initial content load
        if (!isEditorReadyRef.current) return
        const isYjsSync = transaction.getMeta("y-sync$")
        if (!isYjsSync) {
          setContentChanged(true)
        }
      },
    },
    [extensions],
  )

  // Keep the UI-state hook's editor ref current (assignment during render,
  // same pattern as manualSaveRef in useQuestionnaireSave)
  editorInstanceRef.current = editor

  // Editor is editable only while the WebSocket is connected.
  // With IndexedDB persistence removed, edits made during a disconnect have
  // nowhere to be stored — they'd live only in the ephemeral in-memory Y.Doc
  // and be lost on reload. Read-only during any disconnect prevents silent
  // data loss and makes the connection state obvious to the user.
  useEffect(() => {
    if (editor) {
      editor.setEditable(
        collaborationEnabled ? isConnected && !isConnecting : true,
      )
    }
  }, [editor, isConnected, isConnecting, collaborationEnabled])

  // The start screen over an empty questionnaire, and the menu actions its
  // tiles run (QuestionnaireMenuBar fills menuActionRef).
  const menuActionRef = useRef<((key: string) => Promise<void>) | null>(null)
  const startScreen = useStartScreen({
    editor,
    questionnaire,
    inMaster: currentVariant === "",
    ready:
      !collaborationEnabled ||
      (isCollaborationReady && isConnected && !isConnecting),
  })
  startScreenDismissRef.current = startScreen.dismiss

  // Mark editor as ready after initial content is loaded
  // This prevents showing "Unsaved" during initial load
  useEffect(() => {
    if (!editor) return

    // Wait for next tick to ensure initial content updates have fired
    const timeout = setTimeout(() => {
      isEditorReadyRef.current = true
    }, 100)

    return () => clearTimeout(timeout)
  }, [editor])

  // Logic + Recode wizard event wiring and state
  const { logicWizard, recodeWizard } = useEditorWizards({ editor })

  // Yjs content population + pending variant switch processing
  useYjsContentSync({
    editor,
    currentDoc,
    setCurrentDoc,
    currentDocRef,
    collaborationEnabled,
    isCollaborationReady,
    collaborationService,
    getProvider,
    switchToVariant,
    currentVariant,
    questionnaire,
    pendingVariantSwitchRef,
    populatedVariantsRef,
  })

  useImperativeHandle(ref, () => {
    const editorInterface = {
      /**
       * Inserts content at the end of the editor.
       *
       * @function insertContentAtEnd
       * @param text - The text to insert
       */
      insertContentAtEnd: (text: string): void => {
        if (editor && !editor.isDestroyed) {
          const endPosition = editor.state.doc.content.size
          editor
            .chain()
            .focus()
            .insertContentAt(
              endPosition,
              text
                .split("\n")
                .map((s: string) => "<p>" + escapeHTML(s) + "</p>")
                .join(""),
            )
            .run()
        }
      },
      /**
       * Retrieves the current HTML content of the editor.
       *
       * @function getHTML
       * @returns The HTML content of the editor
       */
      getHTML: (): string => {
        return editor && !editor.isDestroyed ? editor.getHTML() : ""
      },
      /**
       * Replaces the editor's HTML content via a proper ProseMirror transaction.
       *
       * Uses `replaceWith` on a transaction (rather than `editor.commands.setContent`)
       * so the y-prosemirror binding can diff the change against the existing Yjs doc
       * and produce minimal operations. `setContent` would instead create brand-new
       * Yjs ops for every node, piled on top of the existing ops, causing content
       * duplication in collaborative mode.
       *
       * @function setHTML
       * @param html - The HTML content to set
       */
      setHTML: (html: string): void => {
        if (!editor || editor.isDestroyed) return
        const element = document.createElement("div")
        element.innerHTML = html
        const newDoc = ProseMirrorDOMParser.fromSchema(editor.schema).parse(element)
        const { state, view } = editor
        view.dispatch(
          state.tr.replaceWith(0, state.doc.content.size, newDoc.content)
        )
      },
      /**
       * Scrolls to a specific line number in the editor.
       *
       * @function scrollToLine
       * @param lineNumber - The line number to scroll to
       */
      scrollToLine: (lineNumber: number): void => {
        scrollToLine(lineNumber)
      },
      /**
       * The TipTap editor itself, for callers that edit it with transactions
       * (the assistant applies questionnaire edits this way).
       *
       * @function getEditor
       * @returns The editor, or null before it is created or after it is destroyed
       */
      getEditor: (): TiptapEditor | null =>
        editor && !editor.isDestroyed ? editor : null,
    }

    // Update internal ref for structure panel
    internalEditorRef.current = editorInterface

    return editorInterface
  })

  // Save workflow: shared performSave, autosave interval, manual save + Ctrl+S
  const { isSaving, performSave, manualSaveQuestionnaire, runExclusiveSave } =
    useQuestionnaireSave({
      editor,
      collaborationEnabled,
      isConnected,
      isCollaborationReady,
      currentVariant,
      variants,
      fullQuestionnaire,
      fetchLatestQuestionnaire,
      saveQuestionnaire,
      convertToTextFormat,
      getProvider,
      collaborationService,
      contentChanged,
      setContentChanged,
      setEditorContent,
    })

  // Line numbers rendering using extracted hook
  useLineNumbers({
    editor,
    lineNumbersEnabled,
    editorContainerRef,
    lineNumbersRef,
  })

  /**
   * Handle template drop from AddElementModal into editor
   */
  const handleTemplateDrop = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes("application/template")) {
      e.preventDefault()
      e.stopPropagation()

      const templateKey = e.dataTransfer.getData("application/template")
      if (!templateKey || !editor) return

      const actionMap = actions(editor)
      const action = actionMap[templateKey as keyof typeof actionMap]

      if (action && typeof action === "function") {
        action()
      }
    }
  }

  /**
   * Checks the questionnaire for errors and warnings by its ID.
   *
   * @async
   * @function checkQuestionnaire
   * @param id - The ID of the questionnaire to check
   */
  const checkQuestionnaire = async (id: string): Promise<void> => {
    try {
      await performSave() //Save the content before checking
      if (setCheckingQuestionnaireEnabled) {
        setCheckingQuestionnaireEnabled(true)
      }
      const response = await checkQuestionnaireByID(id)
      if (setCheckQuestionnaireErrors) {
        setCheckQuestionnaireErrors(
          response.data.result.notifications.errors || [],
        )
      }
      if (setCheckQuestionnaireWarnings) {
        setCheckQuestionnaireWarnings(
          (response.data.result.notifications
            .unfinished as NotificationWarning[]) || [],
        )
      }
    } catch (error) {
      console.error("Error checking questionnaire:", error)
    }
  }

  /**
   * Scrolls the editor to a specific line number.
   *
   * @function scrollToLine
   * @param lineNumber - The line number to scroll to
   */
  const scrollToLine = (lineNumber: number): void => {
    if (!editor) return

    const lines = editor.view.dom.querySelectorAll(
      ".ProseMirror > *",
    ) as NodeListOf<HTMLElement>
    if (lineNumber < 1 || lineNumber > lines.length) return // Prevent out-of-range

    const lineY = lines[lineNumber - 1].offsetTop
    editorContainerRef.current?.scrollTo({
      top: lineY,
      behavior: "smooth", // Smooth scroll for better UX
    })
  }

  // Handle pending scroll after variant switch
  usePendingScroll({
    pendingScrollLine,
    onClearPendingScroll,
    editor,
    editorContainerRef,
  })

  // The project history panel: open and close, `?history=<seq>` links, restore and jump to a question
  const versionHistory = useVersionHistory({
    editor,
    editorRef: internalEditorRef,
    collaborationEnabled,
    isConnected,
    collaborationService,
    currentVariant,
    variants,
    loadQuestionnaireText,
    saveQuestionnaire,
    runExclusiveSave,
  })

  // Comments hook for inline feedback system (must be before context menu to provide onAddComment)
  const {
    comments,
    activeCommentId,
    isPanelOpen: isCommentsPanelOpen,
    isFormOpen: isCommentFormOpen,
    formSelectedText,
    formPosition,
    closePanel: closeCommentsPanel,
    togglePanel: toggleCommentsPanel,
    closeCommentForm,
    openCommentForm,
    addComment,
    updateComment,
    addReply,
    deleteComment,
    deleteReply,
    setStatus: setCommentStatus,
    resolveComment,
    setActiveComment,
    navigateToComment,
  } = useComments({
    editor,
    projectId: projectId || "",
    variantName: currentVariant,
    userId: userId || "",
    userName: userId || "Anonymous",
    owner: owner,
    enabled: true,
    // Enable real-time collaboration for comments
    collaborationEnabled,
    websocketUrl: websocketUrl || getCollaborationWsUrl(),
  })

  // Context menu hook (must be after editor and scrollToLine are defined)
  const {
    highlightedElement,
    contextMenuPosition,
    contextMenuItems,
    handleClick,
    handleContextMenu,
    closeContextMenu,
    handleMenuClick,
    listPickerState,
    availableLists,
    refreshHighlight,
    confirmationModal,
    closeConfirmationModal,
    usagesPanel,
    closeUsagesPanel,
    exceptionsUsagesPanel,
    closeExceptionsUsagesPanel,
    scrollToLine: scrollToLineFromContextMenu,
    timingModal,
    closeTimingModal,
    handleTimingConfirm,
  } = useEditorContextMenu({
    editorContainerRef,
    editor,
    editorRef: internalEditorRef,
    onOpenStructurePanel: jumpToStructure,
    onOpenLabelsModal,
    onContentChange: () => setContentChanged(true),
    onAddComment: openCommentForm,
  })

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      contentChanged !== false &&
      currentLocation.pathname !== nextLocation.pathname,
  )

  return (
    <div className="flex flex-col h-full">
      <ConfirmationModal
        title="Unsaved Changes"
        isVisible={blocker.state === "blocked"}
        message="You have unsaved changes, are you sure you want to leave?"
        cancel={() => blocker.reset?.()}
        confirm={() => blocker.proceed?.()}
        confirmButtonText="Leave"
        confirmButtonType="danger"
      />
      <QuestionnaireMenuBar
        inMaster={inMaster}
        qnrEmpty={qnrEmpty}
        variants={variants}
        masterLanguages={masterLanguages}
        editor={editor}
        highlightingEnabled={highlightingEnabled}
        toggleHighlighting={toggleHighlighting}
        checkQuestionnaire={checkQuestionnaire}
        getQuestionnaireNotes={getQuestionnaireNotes}
        toggleLineNumbers={toggleLineNumbers}
        lineNumbersEnabled={lineNumbersEnabled}
        elementHighlightingEnabled={elementHighlightingEnabled}
        toggleElementHighlighting={toggleElementHighlighting}
        toggleStructurePanel={toggleStructurePanel}
        structurePanelVisible={structurePanelVisible}
        toggleAddElementModal={toggleAddElementModal}
        addElementModalVisible={addElementModalVisible}
        refreshHighlight={refreshHighlight}
        structurePanelButtonRef={structurePanelButtonRef}
        addElementButtonRef={addElementButtonRef}
        elementHighlightButtonRef={elementHighlightButtonRef}
        isConnecting={isConnecting}
        toggleCommentsPanel={toggleCommentsPanel}
        commentsPanelVisible={isCommentsPanelOpen}
        commentsCount={comments.length}
        hasNewComments={hasNewComments}
        toggleHistoryPanel={versionHistory.togglePanel}
        historyPanelVisible={versionHistory.isPanelOpen}
        currentVariant={currentVariant}
        onSave={manualSaveQuestionnaire}
        toolbarExtras={toolbarExtras}
        templateProposalRef={templateProposalRef}
        menuActionRef={menuActionRef}
      />
      <StructurePanel
        editorRef={internalEditorRef}
        isVisible={structurePanelVisible}
        scrollToLine={scrollToLine}
        editor={editor}
        onClose={toggleStructurePanel}
        addElementModalVisible={addElementModalVisible}
        setAddElementModalVisible={setAddElementModalVisible}
        jumpToLineNumber={structureJumpToLine}
        onJumpComplete={() => setStructureJumpToLine(undefined)}
      />
      <div
        className={`flex flex-col flex-grow overflow-hidden ${
          structurePanelVisible ? "editor-with-structure-panel" : ""
        }`}
      >
        <div
          ref={editorContainerRef}
          className="flex relative overflow-y-auto w-full"
          style={{
            height: checkingQuestionnaireEnabled
              ? `${100 - checkDisplayHeight}%`
              : "100%",
          }}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes("application/template")) {
              e.preventDefault()
              e.dataTransfer.dropEffect = "copy"
            }
          }}
          onDrop={handleTemplateDrop}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
        >
          {lineNumbersEnabled && (
            <div
              ref={lineNumbersRef}
              className="line-numbers-questionnaire border-gray-300"
              style={{ position: "absolute", left: 0, top: 0 }}
            ></div>
          )}
          <div className={lineNumbersEnabled ? "ml-10 w-full" : "w-full"}>
            <EditorContent className="h-full w-full" editor={editor} />
          </div>

          {startScreen.visible && (
            <EditorStartScreen
              onUploadWord={() => {
                void menuActionRef.current?.("convert_questionnaire_ai")
              }}
              onTemplate={() => {
                // Same path as Insert > Template: save first, then the gallery.
                void menuActionRef.current?.("template")
              }}
              onBlank={() => {
                startScreen.dismiss()
                editor?.commands.focus()
              }}
            />
          )}

          {/* Connection overlay — editor is read-only whenever the WebSocket
              isn't connected (initial connect OR mid-session disconnect). */}
          {collaborationEnabled && (isConnecting || !isConnected) && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="flex flex-col items-center gap-3">
                <Loader fontSize={32} />
                <p className="text-gray-600 text-sm">
                  {isConnecting
                    ? "Connecting to collaboration server..."
                    : "Reconnecting… editing is paused."}
                </p>
              </div>
            </div>
          )}

          {/* Element Highlight Overlay */}
          {highlightedElement && elementHighlightingEnabled && (
            <ElementHighlight
              element={highlightedElement}
              containerRef={editorContainerRef}
            />
          )}
        </div>

        {checkingQuestionnaireEnabled && (
          <div
            className="overflow-hidden border-t border-gray-300 flex flex-col"
            style={{ height: `${checkDisplayHeight}%` }}
          >
            <div
              className="h-0.5 bg-gray-100 hover:bg-[var(--dq-brand)] cursor-ns-resize flex-shrink-0 transition-colors"
              onMouseDown={handleCheckDisplayResize}
            />
            <QuestionnaireCheckDisplay
              setCheckingQuestionnaireEnabled={
                setCheckingQuestionnaireEnabled || (() => {})
              }
              checkQuestionnaireErrors={checkQuestionnaireErrors}
              checkQuestionnaireWarnings={checkQuestionnaireWarnings}
              checkQuestionnaire={checkQuestionnaire}
              isCheckingQuestionnaire={isCheckingQuestionnaire}
              handleErrorClick={scrollToLine}
              currentVariant={currentVariant}
              onSwitchVariant={onSwitchVariant}
              variants={variants}
              setPendingScroll={onSetPendingScroll}
            />
          </div>
        )}
      </div>
      <EditorFooter
        isSaving={isSaving}
        contentChanged={contentChanged}
        isCollaborationConnected={isConnected && collaborationEnabled}
        isConnecting={isConnecting}
        onSave={manualSaveQuestionnaire}
        provider={collaborationEnabled ? getProvider(currentVariant === "" ? "master" : currentVariant) : null}
        currentUserId={userId}
      />

      {/* Context Menu */}
      <EditorContextMenu
        isVisible={contextMenuPosition !== null}
        position={contextMenuPosition}
        items={contextMenuItems}
        onClick={handleMenuClick}
        onClose={closeContextMenu}
      />

      {/* List Picker Modal */}
      <ListPickerModal
        isVisible={listPickerState.isVisible}
        onClose={listPickerState.onClose}
        availableLists={availableLists}
        onSelectList={listPickerState.onSelect}
        title={
          listPickerState.mode === "FROM"
            ? "Select List for FROM"
            : listPickerState.mode === "PER"
              ? "Select List for PER"
              : "Select a List"
        }
        description={
          listPickerState.mode === "FROM"
            ? "Choose a list to use with FROM in your question"
            : listPickerState.mode === "PER"
              ? "Choose a list to use with PER in your question"
              : "Choose a list to insert"
        }
      />

      {/* Confirmation Modal */}
      {confirmationModal && (
        <ConfirmationModal
          isVisible={confirmationModal.isVisible}
          title={confirmationModal.title}
          message={confirmationModal.message}
          confirmButtonText={confirmationModal.confirmButtonText}
          confirmButtonType={confirmationModal.confirmButtonType}
          confirm={confirmationModal.onConfirm}
          cancel={confirmationModal.onCancel || closeConfirmationModal}
          alternativeAction={confirmationModal.alternativeAction}
        />
      )}

      {/* Usages Panel */}
      {usagesPanel && usagesPanel.isVisible && (
        <UsagesPanel
          listName={usagesPanel.listName}
          usages={usagesPanel.usages}
          onLineClick={scrollToLineFromContextMenu}
          onClose={closeUsagesPanel}
          editorContainerRef={editorContainerRef}
        />
      )}

      {/* Exceptions Usages Panel */}
      {exceptionsUsagesPanel && exceptionsUsagesPanel.isVisible && (
        <ExceptionsUsagesPanel
          exceptionItems={exceptionsUsagesPanel.exceptionItems}
          onLineClick={scrollToLineFromContextMenu}
          onClose={closeExceptionsUsagesPanel}
          editorContainerRef={editorContainerRef}
        />
      )}

      {/* Timing Modal */}
      {timingModal && (
        <TimingModal
          isVisible={timingModal.isVisible}
          onClose={closeTimingModal}
          onConfirm={handleTimingConfirm}
          elementType={timingModal.elementType}
          currentTiming={timingModal.currentTiming}
        />
      )}

      {/* Logic Wizard Modal — opened via the IfTriggerExtension.
          The `key` is tied to (initialText, targetFrom, targetTo) so that
          opening the wizard on a different IF line forces a clean remount
          instead of mutating an existing instance — letting LogicWizard
          rely on lazy useState initialisers without a state-syncing effect. */}
      {logicWizard.state && (
        <LogicWizard
          key={`${logicWizard.state.targetFrom}-${logicWizard.state.targetTo}-${logicWizard.state.initialText ?? ""}`}
          isOpen={logicWizard.state.isOpen}
          onClose={logicWizard.close}
          onApply={logicWizard.apply}
          questionnaireHtml={editor ? editor.getHTML() : ""}
          initialIfText={logicWizard.state.initialText}
        />
      )}

      {/* Recode Wizard Modal — opened via the RecodeTriggerExtension. Same
          remount-on-target pattern as the Logic Wizard above. */}
      {recodeWizard.state && (
        <RecodeWizard
          key={`${recodeWizard.state.targetFrom}-${recodeWizard.state.targetTo}-${recodeWizard.state.initialText ?? ""}`}
          isOpen={recodeWizard.state.isOpen}
          onClose={recodeWizard.close}
          onApply={recodeWizard.apply}
          questionnaireHtml={editor ? editor.getHTML() : ""}
          initialRecodeText={recodeWizard.state.initialText}
        />
      )}

      {/* Comments UI: panel, add/reply forms, delete confirmations */}
      <EditorCommentsUI
        comments={comments}
        activeCommentId={activeCommentId}
        isPanelOpen={isCommentsPanelOpen}
        isFormOpen={isCommentFormOpen}
        formSelectedText={formSelectedText}
        formPosition={formPosition}
        closePanel={closeCommentsPanel}
        closeCommentForm={closeCommentForm}
        addComment={addComment}
        updateComment={updateComment}
        addReply={addReply}
        deleteComment={deleteComment}
        deleteReply={deleteReply}
        setCommentStatus={setCommentStatus}
        resolveComment={resolveComment}
        setActiveComment={setActiveComment}
        navigateToComment={navigateToComment}
        newCommentIds={newCommentIds}
        onExportAuditLog={() => exportAuditLog(projectId)}
        editorContainerRef={editorContainerRef}
      />

      {/* Version History Panel */}
      <VersionHistoryPanel projectId={projectId || ""} {...versionHistory.panelProps} />

      {/* What's New Tour */}
      <Tour
        open={isTourActive}
        onClose={handleTourClose}
        onFinish={handleTourFinish}
        steps={tourSteps}
        rootClassName="dataq-tour"
        mask={{
          style: {
            boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
          },
        }}
        gap={{
          offset: 6,
          radius: 8,
        }}
        indicatorsRender={(current, total) => (
          <span>
            {current + 1} / {total}
          </span>
        )}
      />
    </div>
  )
})

Editor.displayName = "Editor"

export default Editor
